import { useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { selectUserId } from '../../../../../store/slices/user';
import {
  useUpdateSubscriptionBalanceMutation,
  useGetSubscriptionSummariesByUserIdQuery,
  useGetAllAvailableBalancesFromExchangesQuery,
} from '../../../../../store/services/api';
import { DepositEntry } from '../../../types';

type SubmissionState = 'idle' | 'submitting' | 'success' | 'error';

type SubmissionProgress = {
  current: number;
  total: number;
  currentSub: string;
};

export function useSubmitDeposit() {
  const userId = useSelector(selectUserId);
  const [updateBalance] = useUpdateSubscriptionBalanceMutation();
  const { refetch: refetchSummaries } =
    useGetSubscriptionSummariesByUserIdQuery(userId, {
      refetchOnMountOrArgChange: false,
    });
  const { refetch: refetchBalances } =
    useGetAllAvailableBalancesFromExchangesQuery(userId, {
      refetchOnMountOrArgChange: false,
    });

  const [state, setState] = useState<SubmissionState>('idle');
  const [progress, setProgress] = useState<SubmissionProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [failedSub, setFailedSub] = useState<string | null>(null);

  const submit = useCallback(
    async (changedEntries: DepositEntry[]) => {
      setState('submitting');
      setError(null);
      setFailedSub(null);

      const total = changedEntries.length;
      let step = 0;

      for (const entry of changedEntries) {
        step++;
        setProgress({
          current: step,
          total,
          currentSub: entry.botFriendlyName,
        });

        try {
          await updateBalance({
            userId,
            subscriptionId: entry.subscriptionId,
            stableBalance: entry.currentStableBalance + entry.depositAmount,
            shouldOrder: false,
          }).unwrap();
        } catch (err) {
          setState('error');
          setError(`Deposit failed for ${entry.botFriendlyName}`);
          setFailedSub(entry.botFriendlyName);
          return;
        }
      }

      await Promise.all([refetchSummaries(), refetchBalances()]);
      setState('success');
      setProgress(null);
    },
    [updateBalance, userId, refetchSummaries, refetchBalances],
  );

  const resetState = useCallback(() => {
    setState('idle');
    setProgress(null);
    setError(null);
    setFailedSub(null);
  }, []);

  return {
    submit,
    state,
    progress,
    error,
    failedSub,
    resetState,
  };
}
